import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ArrowLeft, Loader2, FileText, Printer, AlertCircle, X, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { invoiceApiService, InvoiceHeader, InvoiceDetails, OtherCharges } from '../services/invoiceApi';
import { branchApiService, Branch } from '../services/branchApi';
import { customerApiService, Customer } from '../services/customerApi';
import { userApiService } from '../services/userApi';
import { authApiService } from '../services/authApi';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import { formatDate } from '../utils/dateUtils';
import { InvoiceChunkedPrint } from './InvoiceChunkedPrint';
import { getInvoiceConfig, isSupplyTypeWithoutTax } from '../config/uiConfig';

// Simple number-to-words for INR (whole number only)
function amountInWords(num: number): string {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convertHundreds = (n: number): string => {
    let result = '';
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + ' ';
      n %= 10;
    }
    if (n > 0) result += ones[n] + ' ';
    return result.trim();
  };

  if (num === 0) return 'Zero';
  const whole = Math.floor(num);
  const paise = Math.round((num % 1) * 100);
  const numStr = whole.toString();
  let result = '';

  if (numStr.length > 7) {
    const crores = parseInt(numStr.slice(0, numStr.length - 7), 10);
    if (crores > 0) result += convertHundreds(crores) + ' Crore ';
  }
  if (numStr.length > 5) {
    const lakhs = parseInt(numStr.slice(Math.max(0, numStr.length - 7), numStr.length - 5), 10);
    if (lakhs > 0) result += convertHundreds(lakhs) + ' Lakh ';
  }
  if (numStr.length > 3) {
    const thousands = parseInt(numStr.slice(Math.max(0, numStr.length - 5), numStr.length - 3), 10);
    if (thousands > 0) result += convertHundreds(thousands) + ' Thousand ';
  }
  const hundreds = parseInt(numStr.slice(Math.max(0, numStr.length - 3)), 10);
  if (hundreds > 0) result += convertHundreds(hundreds);

  result = result.trim() + ' Rupees';
  if (paise > 0) result += ' and ' + convertHundreds(paise) + ' Paise';
  return result.trim();
}

interface HsnWiseRow {
  hsn: string;
  taxableValue: number;
  cgstRate: number;
  cgstAmount: number;
  sgstRate: number;
  sgstAmount: number;
  igstRate: number;
  igstAmount: number;
  totalTaxAmount: number;
}

// Backend may store tax rate as percentage (18) or basis points (1800 = 18%). Normalize for display.
function toDisplayRate(value: number): number {
  if (value == null || Number.isNaN(value)) return 0;
  return value > 100 ? value / 100 : value;
}

// Effective rate for calculation: if stored as basis points (>100), convert to percentage.
function toEffectiveRate(value: number): number {
  if (value == null || Number.isNaN(value)) return 0;
  return value > 100 ? value / 100 : value;
}

/** E-invoice API may return `{ message, details: { message } }` on HTTP 200 with success: false. */
function parseEinvoiceErrorPayload(res: unknown): { title: string; detail?: string } | null {
  if (res == null || typeof res !== 'object') return null;
  const o = res as Record<string, unknown>;
  if (o.success === true) return null;
  const summary = typeof o.message === 'string' ? o.message.trim() : '';
  let inner = '';
  const d = o.details;
  if (d != null && typeof d === 'object') {
    const m = (d as Record<string, unknown>).message;
    if (typeof m === 'string') inner = m.trim();
  }
  if (!inner && !summary) return { title: 'E-invoice generation failed.' };
  if (inner && summary && inner !== summary) {
    return { title: inner, detail: summary };
  }
  return { title: inner || summary || 'E-invoice generation failed.' };
}

/** E-way bill API may return `{ message, details: { message } }` on HTTP 200 with success: false. */
function parseEwayBillErrorPayload(res: unknown): { title: string; detail?: string } | null {
  if (res == null || typeof res !== 'object') return null;
  const o = res as Record<string, unknown>;
  if (o.success === true) return null;
  const summary = typeof o.message === 'string' ? o.message.trim() : '';
  let inner = '';
  const d = o.details;
  if (d != null && typeof d === 'object') {
    const m = (d as Record<string, unknown>).message;
    if (typeof m === 'string') inner = m.trim();
  }
  if (!inner && !summary) return { title: 'E-way bill generation failed.' };
  if (inner && summary && inner !== summary) {
    return { title: inner, detail: summary };
  }
  return { title: inner || summary || 'E-way bill generation failed.' };
}

const InvoiceViewPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { selectedBranch } = useBranch();
  const { user } = useAuth();

  const [invoice, setInvoice] = useState<InvoiceHeader | null>(null);
  const [branchDetails, setBranchDetails] = useState<Branch | null>(null);
  const [shipToCustomer, setShipToCustomer] = useState<Customer | null>(null);
  const [billToCustomer, setBillToCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [generatingEinvoice, setGeneratingEinvoice] = useState(false);
  const [generatingEwayBill, setGeneratingEwayBill] = useState(false);
  const [einvoiceError, setEinvoiceError] = useState<{ title: string; detail?: string } | null>(null);
  const [ewayBillError, setEwayBillError] = useState<{ title: string; detail?: string } | null>(null);
  const [isEwayDialogOpen, setIsEwayDialogOpen] = useState(false);
  const [isEwayEditMode, setIsEwayEditMode] = useState(false);
  const [ewayForm, setEwayForm] = useState<{
    ewayTransportMode: string;
    ewayTransporterId: string;
    ewayTransporterName: string;
    ewayDistanceKm: string;
    motorVehicleNo: string;
  }>({
    ewayTransportMode: '',
    ewayTransporterId: '',
    ewayTransporterName: '',
    ewayDistanceKm: '',
    motorVehicleNo: '',
  });
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [einvoicePassword, setEinvoicePassword] = useState('');
  const [einvoicePasswordError, setEinvoicePasswordError] = useState('');
  const [validatingPassword, setValidatingPassword] = useState(false);
  const [printCopies, setPrintCopies] = useState(1);
  const [einvoiceSignerName, setEinvoiceSignerName] = useState('');
  const originalDocumentTitleRef = useRef<string | null>(null);

  const reloadInvoice = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setInvoice(null);
      setBranchDetails(null);
      setShipToCustomer(null);
      setBillToCustomer(null);
      setEinvoiceError(null);
      setEwayBillError(null);

      const invRes = await invoiceApiService.getInvoiceHeaderById(id);
      if (!invRes.success || !invRes.data) {
        return;
      }
      const inv = invRes.data;
      const headerBranchId = selectedBranch != null ? Number(selectedBranch) : null;
      const invoiceBranchId = inv.branchId != null ? Number(inv.branchId) : null;
      if (
        headerBranchId == null ||
        invoiceBranchId == null ||
        headerBranchId !== invoiceBranchId
      ) {
        return;
      }

      const branchRes = await branchApiService.getBranchById(String(headerBranchId));
      setInvoice(inv);
      setBranchDetails(branchRes ?? null);

      const shipToId = inv.shipTo ?? inv.billTo;
      const billToId = inv.billTo;
      const [shipRes, billRes] = await Promise.all([
        shipToId ? customerApiService.getCustomerById(shipToId.toString()) : Promise.resolve({ success: false, data: null }),
        billToId ? customerApiService.getCustomerById(billToId.toString()) : Promise.resolve({ success: false, data: null })
      ]);
      if (shipRes.success && shipRes.data) setShipToCustomer(shipRes.data);
      if (billRes.success && billRes.data) setBillToCustomer(billRes.data);
    } catch (e) {
      toast.error('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  }, [id, selectedBranch]);

  useEffect(() => {
    reloadInvoice();
  }, [reloadInvoice]);

  useEffect(() => {
    if (!invoice) {
      setEinvoiceSignerName('');
      return;
    }
    const st = String(invoice.status ?? '')
      .toUpperCase()
      .replace(/-/g, '_');
    if (st !== 'E_INVOICED') {
      setEinvoiceSignerName('');
      return;
    }
    const fromApi = invoice.signedByName?.trim();
    if (fromApi) {
      setEinvoiceSignerName(fromApi);
      return;
    }
    const uid = invoice.signedBy ?? invoice.signed_by;
    if (uid == null || Number.isNaN(Number(uid))) {
      setEinvoiceSignerName('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await userApiService.getUserById(String(uid));
        if (cancelled || !res.success || !res.data) return;
        const u = res.data;
        const display =
          [u.firstName, u.lastName].filter(Boolean).join(' ').trim() ||
          u.displayName ||
          u.fullName ||
          '';
        if (!cancelled) setEinvoiceSignerName(display);
      } catch {
        if (!cancelled) setEinvoiceSignerName('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [invoice]);

  const handleGenerateEinvoice = async () => {
    if (!id) return;
    setEinvoiceError(null);
    try {
      setGeneratingEinvoice(true);
      const res = await invoiceApiService.generateEInvoice(id, true);
      if (res.success) {
        setEinvoiceError(null);
        toast.success('E-invoice generated successfully');
        await reloadInvoice();
      } else {
        const parsed = parseEinvoiceErrorPayload(res);
        if (parsed) setEinvoiceError(parsed);
        const toastText = parsed?.title ?? (res as { message?: string }).message ?? 'Failed to generate e-invoice';
        toast.error(toastText);
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to generate e-invoice';
      setEinvoiceError({ title: msg });
      toast.error(msg);
    } finally {
      setGeneratingEinvoice(false);
    }
  };

  const handleGenerateEWayBill = async () => {
    if (!id) return;
    setEwayBillError(null);
    const irn = String(invoice?.irn ?? '').trim();
    if (!irn) {
      toast.error('IRN is required before generating an e-way bill.');
      return;
    }
    try {
      setGeneratingEwayBill(true);

      const distanceRaw = String(ewayForm.ewayDistanceKm ?? '').trim();
      const distanceNum = distanceRaw ? Number(distanceRaw) : undefined;
      const updatePayload = {
        id: Number(id),
        ewayTransportMode: String(ewayForm.ewayTransportMode ?? '').trim() || undefined,
        ewayTransporterId: String(ewayForm.ewayTransporterId ?? '').trim() || undefined,
        ewayTransporterName: String(ewayForm.ewayTransporterName ?? '').trim() || undefined,
        ewayDistanceKm: distanceNum != null && !Number.isNaN(distanceNum) ? distanceNum : undefined,
        motorVehicleNo: String(ewayForm.motorVehicleNo ?? '').trim() || undefined,
        // Backward-compatible keys (some backends accept snake_case)
        eway_transporter_id: String(ewayForm.ewayTransporterId ?? '').trim() || undefined,
        eway_transporter_name: String(ewayForm.ewayTransporterName ?? '').trim() || undefined,
      };
      const updRes = await invoiceApiService.updateInvoiceHeader(id, updatePayload);
      if (!updRes.success) {
        toast.error((updRes as { message?: string }).message ?? 'Failed to save transport details');
        return;
      }

      const res = await invoiceApiService.generateEWayBill(id);
      if (res.success && res.data) {
        setInvoice(res.data);
        setEwayBillError(null);
        toast.success('E-way bill generated successfully');
      } else if (res.success) {
        await reloadInvoice();
        setEwayBillError(null);
        toast.success('E-way bill generated successfully');
      } else {
        const parsed = parseEwayBillErrorPayload(res);
        if (parsed) setEwayBillError(parsed);
        const toastText =
          parsed?.title ?? (res as { message?: string }).message ?? 'Failed to generate e-way bill';
        toast.error(toastText);
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to generate e-way bill';
      setEwayBillError({ title: msg });
      toast.error(msg);
    } finally {
      setGeneratingEwayBill(false);
    }
  };

  const openEwayDialog = () => {
    setEwayForm({
      ewayTransportMode: String((invoice as any)?.ewayTransportMode ?? '').trim(),
      ewayTransporterId: String((invoice as any)?.ewayTransporterId ?? (invoice as any)?.eway_transporter_id ?? '').trim(),
      ewayTransporterName: String((invoice as any)?.ewayTransporterName ?? (invoice as any)?.eway_transporter_name ?? '').trim(),
      ewayDistanceKm:
        (invoice as any)?.ewayDistanceKm != null && String((invoice as any)?.ewayDistanceKm).trim() !== ''
          ? String((invoice as any).ewayDistanceKm)
          : '',
      motorVehicleNo: String((invoice as any)?.motorVehicleNo ?? '').trim(),
    });
    setIsEwayEditMode(false);
    setIsEwayDialogOpen(true);
  };

  const getEinvoiceValidationError = (): string | null => {
    const customer = billToCustomer;
    const supplierStateCode = String(branchDetails?.stateCode ?? '').trim();
    const customerStateCode = String(customer?.stateCode ?? '').trim();

    if (!customerStateCode) return 'Customer state code is missing. Please update customer master.';
    const gstNo = String(customer?.gstNo ?? '').trim();
    if (!gstNo) return 'Customer GST number is missing. Please update customer master.';

    const address = String(customer?.address ?? '').trim();
    const hasPincode = /\b\d{6}\b/.test(address);
    if (!hasPincode) return 'Customer pincode is missing in address. Please update customer address.';

    const details = invoice?.invoiceDetails ?? [];
    const badUomLine = details.find((d) => {
      const uom = String((d as any)?.uom ?? '').trim();
      return !uom || uom.length < 3;
    });
    if (badUomLine) {
      const name = String((badUomLine as any)?.endgoodName ?? '').trim() || 'an item';
      return `Item UOM is missing/invalid for ${name}. UOM must be at least 3 characters.`;
    }

    const vehicleNo = String(invoice?.motorVehicleNo ?? '').trim();
    if (!vehicleNo) return 'Vehicle number is required. Please enter Motor Vehicle No. in invoice.';

    const isInterState = supplierStateCode && customerStateCode && supplierStateCode !== customerStateCode;
    if (isInterState) {
      const transporterMasterId = (invoice as any)?.transporterMasterId;
      const hasTransporterMaster =
        transporterMasterId != null && !Number.isNaN(Number(transporterMasterId)) && Number(transporterMasterId) > 0;
      const ewayTransporterId = String((invoice as any)?.ewayTransporterId ?? (invoice as any)?.eway_transporter_id ?? '').trim();
      if (!hasTransporterMaster && !ewayTransporterId) {
        return 'Transporter is required for inter-state supply. Please select transporter.';
      }
    }

    return null;
  };

  const openEinvoicePasswordDialog = () => {
    if (!user?.username) {
      toast.error('Current user information is unavailable.');
      return;
    }
    const err = getEinvoiceValidationError();
    if (err) {
      toast.error(err);
      setEinvoiceError({ title: err });
      return;
    }
    setEinvoicePassword('');
    setEinvoicePasswordError('');
    setIsPasswordDialogOpen(true);
  };

  const handlePasswordVerificationAndGenerate = async () => {
    if (!user?.username) {
      toast.error('Current user information is unavailable.');
      return;
    }
    const validationError = getEinvoiceValidationError();
    if (validationError) {
      toast.error(validationError);
      setEinvoiceError({ title: validationError });
      return;
    }
    if (!einvoicePassword.trim()) {
      setEinvoicePasswordError('Please enter your password.');
      return;
    }

    try {
      setEinvoicePasswordError('');
      setValidatingPassword(true);
      await authApiService.authenticateUser(user.username, einvoicePassword);
      setIsPasswordDialogOpen(false);
      setEinvoicePassword('');
      setEinvoicePasswordError('');
      await handleGenerateEinvoice();
    } catch {
      setEinvoicePasswordError('Invalid password.');
    } finally {
      setValidatingPassword(false);
    }
  };

  const handlePrintWithCopies = async () => {
    const input = window.prompt('How many copies do you want to print?', String(printCopies || 1));
    if (input == null) return;
    const n = Math.max(1, Math.min(20, parseInt(input, 10) || 1));
    setPrintCopies(n);
    if (invoice?.invoiceNo) {
      if (originalDocumentTitleRef.current == null) originalDocumentTitleRef.current = document.title;
      document.title = invoice.invoiceNo;
    }
    // Allow state to apply before opening print dialog
    setTimeout(() => window.print(), 50);
  };

  const statusNorm = String(invoice?.status ?? '')
    .toUpperCase()
    .replace(/-/g, '_');
  const isEInvoiced = statusNorm === 'E_INVOICED';
  const invoiceConfig = getInvoiceConfig();
  const enabledEinvoiceBranchCodes = invoiceConfig.einvoiceEnabledBranchCodes.map((code) =>
    String(code).toUpperCase()
  );
  const isEinvoiceEnabledForBranch =
    enabledEinvoiceBranchCodes.length === 0 ||
    enabledEinvoiceBranchCodes.includes(String(branchDetails?.branchCode ?? '').toUpperCase());

  // Default to 4 copies for e-invoice print
  useEffect(() => {
    setPrintCopies(isEInvoiced ? 4 : 1);
  }, [isEInvoiced]);

  useEffect(() => {
    const prev = window.onafterprint;
    window.onafterprint = () => {
      setPrintCopies(isEInvoiced ? 4 : 1);
      if (originalDocumentTitleRef.current != null) {
        document.title = originalDocumentTitleRef.current;
        originalDocumentTitleRef.current = null;
      }
      if (typeof prev === 'function') prev.call(window, new Event('afterprint'));
    };
    return () => {
      window.onafterprint = prev ?? null;
    };
  }, [isEInvoiced]);

  const isGst = useMemo(() => {
    if (!branchDetails || !billToCustomer) return true;
    return (branchDetails.stateCode ?? '') !== (billToCustomer.stateCode ?? '');
  }, [branchDetails, billToCustomer]);

  const calculationDetails = useMemo(() => {
    if (!invoice?.invoiceDetails) return null;
    const details = invoice.invoiceDetails;
    const taxableAmount = details.reduce((s, d) => s + (d.qty * d.rate), 0);
    // Detect whether line-level cgst/sgst/igst are stored as AMOUNTS or RATES.
    const sumLineTaxFields = details.reduce(
      (s, d) => s + (d.cgst ?? 0) + (d.sgst ?? 0) + (d.igst ?? 0),
      0
    );
    const headerTax =
      invoice.grandTotalTax != null && !Number.isNaN(Number(invoice.grandTotalTax))
        ? Number(invoice.grandTotalTax)
        : 0;
    let taxFieldsAreAmounts = false;
    if (headerTax > 0) {
      const diff = Math.abs(sumLineTaxFields - headerTax);
      if (diff <= 0.01 * Math.max(headerTax, 1)) {
        taxFieldsAreAmounts = true;
      }
    } else {
      // Fallback heuristic: if any stored value is unrealistically large for a rate (> 100%), treat as amount
      taxFieldsAreAmounts = details.some(
        (d) => (d.cgst ?? 0) > 100 || (d.sgst ?? 0) > 100 || (d.igst ?? 0) > 100
      );
    }

    let totalCgst = 0;
    let totalSgst = 0;
    let totalIgst = 0;
    if (taxFieldsAreAmounts) {
      // Newer invoices where cgst/sgst/igst are stored as amounts (₹)
      totalCgst = details.reduce((s, d) => s + (d.cgst ?? 0), 0);
      totalSgst = details.reduce((s, d) => s + (d.sgst ?? 0), 0);
      totalIgst = details.reduce((s, d) => s + (d.igst ?? 0), 0);
    } else {
      // Older invoices where cgst/sgst/igst are stored as rates (% or basis points)
      details.forEach((d) => {
        const amt = d.qty * d.rate;
        if (isGst) {
          totalIgst += (amt * toEffectiveRate(d.igst ?? 0)) / 100;
        } else {
          totalCgst += (amt * toEffectiveRate(d.cgst ?? 0)) / 100;
          totalSgst += (amt * toEffectiveRate(d.sgst ?? 0)) / 100;
        }
      });
    }

    const computedTotalTax = totalCgst + totalSgst + totalIgst;
    const otherChargesTotal = (invoice.otherCharges ?? []).reduce((s, c) => s + (c.amount || 0), 0);
    const totalTax =
      headerTax > 0
        ? headerTax
        : computedTotalTax;
    const totalAmount =
      invoice.grandTotal != null && !Number.isNaN(Number(invoice.grandTotal))
        ? Number(invoice.grandTotal)
        : taxableAmount + totalTax + otherChargesTotal;
    const totalBoxQty = details.reduce((s, d) => {
      const boxes = d.boxes ? parseInt(String(d.boxes).replace(/\D/g, ''), 10) : 0;
      return s + (isNaN(boxes) ? 0 : boxes);
    }, 0);
    const totalQty = details.reduce((s, d) => s + (d.qty ?? 0), 0);
    return {
      taxableAmount,
      totalCgst,
      totalSgst,
      totalIgst,
      totalTax,
      otherChargesTotal,
      totalAmount,
      totalBoxQty,
      totalQty,
      totalTaxableAmount: totalTax
    };
  }, [invoice, isGst]);

  const hsnWiseTaxDetails = useMemo((): HsnWiseRow[] => {
    if (!invoice?.invoiceDetails) return [];
    const details = invoice.invoiceDetails;
    const supplyType = String(
      (invoice as { supTyp?: string; sup_typ?: string; supplyType?: string }).supTyp ??
        (invoice as { sup_typ?: string }).sup_typ ??
        (invoice as { supplyType?: string }).supplyType ??
        ''
    ).trim();
    const supplyWithoutTax = isSupplyTypeWithoutTax(supplyType);

    // Reuse same detection as above for amount vs rate semantics
    const sumLineTaxFields = details.reduce(
      (s, d) => s + (d.cgst ?? 0) + (d.sgst ?? 0) + (d.igst ?? 0),
      0
    );
    const headerTax =
      invoice.grandTotalTax != null && !Number.isNaN(Number(invoice.grandTotalTax))
        ? Number(invoice.grandTotalTax)
        : 0;
    let taxFieldsAreAmounts = false;
    if (headerTax > 0) {
      const diff = Math.abs(sumLineTaxFields - headerTax);
      if (diff <= 0.01 * Math.max(headerTax, 1)) {
        taxFieldsAreAmounts = true;
      }
    } else {
      taxFieldsAreAmounts = details.some(
        (d) => (d.cgst ?? 0) > 100 || (d.sgst ?? 0) > 100 || (d.igst ?? 0) > 100
      );
    }

    const byHsn: Record<
      string,
      { taxableValue: number; cgstAmount: number; sgstAmount: number; igstAmount: number }
    > = {};

    details.forEach((d) => {
      const hsn = d.hsn || 'N/A';
      const amt = d.qty * d.rate;
      if (!byHsn[hsn]) {
        byHsn[hsn] = { taxableValue: 0, cgstAmount: 0, sgstAmount: 0, igstAmount: 0 };
      }
      byHsn[hsn].taxableValue += amt;
      if (taxFieldsAreAmounts) {
        // Newer invoices: line fields are amounts
        byHsn[hsn].cgstAmount += d.cgst ?? 0;
        byHsn[hsn].sgstAmount += d.sgst ?? 0;
        byHsn[hsn].igstAmount += d.igst ?? 0;
      } else {
        // Older invoices: line fields are rates (% or basis points)
        if (isGst) {
          byHsn[hsn].igstAmount += (amt * toEffectiveRate(d.igst ?? 0)) / 100;
        } else {
          byHsn[hsn].cgstAmount += (amt * toEffectiveRate(d.cgst ?? 0)) / 100;
          byHsn[hsn].sgstAmount += (amt * toEffectiveRate(d.sgst ?? 0)) / 100;
        }
      }
    });
    return Object.entries(byHsn).map(([hsn, v]) => {
      const tv = v.taxableValue;
      let cgstRate = 0;
      let sgstRate = 0;
      let igstRate = 0;
      if (taxFieldsAreAmounts) {
        // Derive effective % rate from amounts
        cgstRate = tv > 0 ? (v.cgstAmount / tv) * 100 : 0;
        sgstRate = tv > 0 ? (v.sgstAmount / tv) * 100 : 0;
        igstRate = tv > 0 ? (v.igstAmount / tv) * 100 : 0;
      } else {
        // Use stored percentages from one of the lines (may be % or basis points)
        const firstDetail = details.find((d) => (d.hsn || 'N/A') === hsn);
        cgstRate = toDisplayRate(firstDetail?.cgst ?? 0);
        sgstRate = toDisplayRate(firstDetail?.sgst ?? 0);
        igstRate = toDisplayRate(firstDetail?.igst ?? 0);
      }
      if (supplyWithoutTax) {
        cgstRate = 0;
        sgstRate = 0;
        igstRate = 0;
      }
      const totalTaxAmount = v.cgstAmount + v.sgstAmount + v.igstAmount;
      return {
        hsn,
        taxableValue: tv,
        cgstRate,
        cgstAmount: v.cgstAmount,
        sgstRate,
        sgstAmount: v.sgstAmount,
        igstRate,
        igstAmount: v.igstAmount,
        totalTaxAmount
      };
    });
  }, [invoice, isGst]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="container mx-auto p-6 space-y-4">
        <Button variant="outline" onClick={() => navigate('/invoices')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Invoices
        </Button>
        <Card className="border-primary-200 shadow-md">
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-700">Invoice not found</p>
            <p className="text-sm text-gray-500 mt-2">
              This invoice does not exist or is not available for the selected branch.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const details = invoice.invoiceDetails ?? [];
  const otherChargesList = invoice.otherCharges ?? [];
  const calc = calculationDetails!;
  // statusNorm / isEInvoiced computed above
  const hasEwayBillNo = invoice.ewayBillNo != null && String(invoice.ewayBillNo).trim() !== '';
  const hasIrn = String(invoice.irn ?? '').trim() !== '';
  const showEwayBillOnView = isEInvoiced && hasEwayBillNo;
  const canOfferGenerateEwayBill = isEInvoiced && !hasEwayBillNo;
  const qrValueRaw =
    (invoice as any).signedQrcode ||
    (invoice as any).signedQRcode ||
    (invoice as any).signedQRCode ||
    (invoice as any).signedQrCode ||
    (invoice as any).qrCode ||
    (invoice as any).qrString ||
    '';
  const qrValue = String(qrValueRaw ?? '')
    .trim()
    .replace(/^"(.*)"$/, '$1');

  const copies = isEInvoiced ? Math.max(1, printCopies) : 1;

  return (
    <div className="container mx-auto p-6 space-y-4">
      {einvoiceError && (
        <Alert variant="destructive" className="print:hidden relative pr-10">
          <AlertCircle className="size-4" />
          <AlertTitle className="line-clamp-none whitespace-normal">E-invoice could not be generated</AlertTitle>
          <AlertDescription className="text-left col-start-2">
            <p className="font-medium text-destructive">{einvoiceError.title}</p>
            {einvoiceError.detail ? (
              <p className="mt-2 text-sm opacity-90">{einvoiceError.detail}</p>
            ) : null}
          </AlertDescription>
          <button
            type="button"
            onClick={() => setEinvoiceError(null)}
            className="absolute right-3 top-3 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label="Dismiss error"
          >
            <X className="h-4 w-4" />
          </button>
        </Alert>
      )}
      {ewayBillError && (
        <Alert variant="destructive" className="print:hidden relative pr-10">
          <AlertCircle className="size-4" />
          <AlertTitle className="line-clamp-none whitespace-normal">E-way bill could not be generated</AlertTitle>
          <AlertDescription className="text-left col-start-2">
            <p className="font-medium text-destructive">{ewayBillError.title}</p>
            {ewayBillError.detail ? (
              <p className="mt-2 text-sm opacity-90">{ewayBillError.detail}</p>
            ) : null}
          </AlertDescription>
          <button
            type="button"
            onClick={() => setEwayBillError(null)}
            className="absolute right-3 top-3 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label="Dismiss error"
          >
            <X className="h-4 w-4" />
          </button>
        </Alert>
      )}

      {/* Header Actions - Hidden when printing */}
      <div className="flex items-center justify-between mb-6 print:hidden">
        <Button variant="outline" onClick={() => navigate('/invoices')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Invoices
        </Button>
        {isEInvoiced ? (
          <div className="flex flex-wrap items-center gap-2">
            {canOfferGenerateEwayBill ? (
              <Button
                type="button"
                variant="outline"
                onClick={openEwayDialog}
                disabled={generatingEwayBill || !hasIrn}
                title={
                  !hasIrn
                    ? 'IRN is required before generating an e-way bill.'
                    : 'Generate e-way bill (document) via GST portal'
                }
                className="border-primary-200 text-primary-800 hover:bg-primary-50"
              >
                {generatingEwayBill ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Truck className="h-4 w-4 mr-2" />
                )}
                {generatingEwayBill ? 'Generating…' : 'Generate e-way bill'}
              </Button>
            ) : null}
            <Button onClick={handlePrintWithCopies} className="bg-primary-600 hover:bg-primary-700 text-white">
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/invoices/${id}/edit`)}>
              Edit Invoice
            </Button>
            {isEinvoiceEnabledForBranch && (
              <Button
                onClick={openEinvoicePasswordDialog}
                disabled={generatingEinvoice || validatingPassword}
                className="bg-primary-600 hover:bg-primary-700 text-white"
              >
                <FileText className="h-4 w-4 mr-2" />
                {generatingEinvoice ? 'Generating...' : 'Generate e-invoice'}
              </Button>
            )}
          </div>
        )}
      </div>

      <Dialog
        open={isEwayDialogOpen}
        onOpenChange={(open) => {
          if (generatingEwayBill) return;
          setIsEwayDialogOpen(open);
          if (!open) setIsEwayEditMode(false);
        }}
      >
        <DialogContent className="sm:max-w-[680px]">
          <DialogHeader>
            <DialogTitle>Transport details for e-way bill</DialogTitle>
            <DialogDescription>
              Review the transport details below. Enable edit if you want to update them before generating the e-way bill.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">
              Invoice: <span className="font-medium text-foreground">{invoice?.invoiceNo ?? ''}</span>
            </div>
            <Button type="button" variant="outline" onClick={() => setIsEwayEditMode((v) => !v)}>
              {isEwayEditMode ? 'View mode' : 'Edit'}
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ewayTransportMode">Transport mode</Label>
              <Input
                id="ewayTransportMode"
                value={ewayForm.ewayTransportMode}
                onChange={(e) => setEwayForm((p) => ({ ...p, ewayTransportMode: e.target.value }))}
                disabled={!isEwayEditMode}
                placeholder="Road / Air / Rail / Ship"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="motorVehicleNo">Vehicle no.</Label>
              <Input
                id="motorVehicleNo"
                value={ewayForm.motorVehicleNo}
                onChange={(e) => setEwayForm((p) => ({ ...p, motorVehicleNo: e.target.value }))}
                disabled={!isEwayEditMode}
                placeholder="e.g. MH12AB1234"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ewayDistanceKm">Distance (KM)</Label>
              <Input
                id="ewayDistanceKm"
                value={ewayForm.ewayDistanceKm}
                onChange={(e) => setEwayForm((p) => ({ ...p, ewayDistanceKm: e.target.value }))}
                disabled={!isEwayEditMode}
                inputMode="numeric"
                placeholder="e.g. 120"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ewayTransporterId">Transporter ID</Label>
              <Input
                id="ewayTransporterId"
                value={ewayForm.ewayTransporterId}
                onChange={(e) => setEwayForm((p) => ({ ...p, ewayTransporterId: e.target.value }))}
                disabled={!isEwayEditMode}
                placeholder="Transporter ID"
              />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="ewayTransporterName">Transporter name</Label>
              <Input
                id="ewayTransporterName"
                value={ewayForm.ewayTransporterName}
                onChange={(e) => setEwayForm((p) => ({ ...p, ewayTransporterName: e.target.value }))}
                disabled={!isEwayEditMode}
                placeholder="Transporter name"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setIsEwayDialogOpen(false)} disabled={generatingEwayBill}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={async () => {
                setIsEwayDialogOpen(false);
                await handleGenerateEWayBill();
              }}
              disabled={generatingEwayBill}
              className="bg-primary-600 hover:bg-primary-700 text-white"
            >
              {generatingEwayBill ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  <Truck className="h-4 w-4 mr-2" />
                  Save & generate
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isPasswordDialogOpen}
        onOpenChange={(open) => {
          if (validatingPassword) return;
          setIsPasswordDialogOpen(open);
          if (!open) {
            setEinvoicePassword('');
            setEinvoicePasswordError('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Password</DialogTitle>
            <DialogDescription>
              Enter your password to generate the e-invoice.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="einvoice-password">Password</Label>
            <Input
              id="einvoice-password"
              type="password"
              value={einvoicePassword}
              onChange={(e) => {
                setEinvoicePassword(e.target.value);
                if (einvoicePasswordError) setEinvoicePasswordError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handlePasswordVerificationAndGenerate();
                }
              }}
              placeholder="Enter your password"
              disabled={validatingPassword}
              className={einvoicePasswordError ? 'border-red-500 focus-visible:ring-red-500' : undefined}
            />
            {einvoicePasswordError ? (
              <p className="text-sm text-red-600">{einvoicePasswordError}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsPasswordDialogOpen(false);
                setEinvoicePassword('');
                setEinvoicePasswordError('');
              }}
              disabled={validatingPassword}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handlePasswordVerificationAndGenerate()}
              disabled={validatingPassword}
              className="bg-primary-600 hover:bg-primary-700 text-white"
            >
              {validatingPassword ? 'Verifying...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!isEInvoiced && (
      <div>
      {Array.from({ length: copies }).map((_, copyIndex) => {
        return (
          <React.Fragment key={copyIndex}>
            <div className={`${copyIndex === 0 ? '' : 'hidden print:block'}`}>
              <div className="invoice-container border rounded-lg p-6 bg-white shadow-sm print:shadow-none print:border print:border-gray-300">
                <div className="flex items-start justify-center gap-6 mt-2 mb-0">
                  <h2 className="text-center font-bold text-xl">Tax Invoice</h2>
                </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 print:grid-cols-2 gap-6 mb-6 min-w-0">
          {/* Left: Seller, Consignee, Buyer */}
          <div className="space-y-4 border rounded p-4 min-w-0">
            <div className="border-b pb-3">
              <div className="flex gap-4">
                {branchDetails && (
                  <>
                    <div className="flex-shrink-0">
                      <img src="/customer-logo/sg-group-logo.jpg" alt="SG Group" className="h-14 w-20 object-contain" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">{branchDetails.branchName}</p>
                      <p className="text-xs text-gray-600 leading-snug">{branchDetails.address}</p>
                      <p className="text-xs text-gray-600 leading-snug">GSTIN/UIN: {branchDetails.gstin || 'NA'}</p>
                      <p className="text-xs text-gray-600 leading-snug">State Name: {branchDetails.stateName || 'NA'}, Code: {branchDetails.stateCode || 'NA'}</p>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="border-b pb-3">
              <p className="font-semibold mb-2">Consignee (Ship to):</p>
              {shipToCustomer ? (
                <div>
                  <p className="text-sm font-medium text-gray-900">{shipToCustomer.customerName}</p>
                  <p className="text-xs text-gray-600 leading-snug mt-0.5">{shipToCustomer.address}</p>
                  <p className="text-xs text-gray-600 leading-snug">Vendor Code: {shipToCustomer.vendorCode ?? 'NA'}</p>
                  <p className="text-xs text-gray-600 leading-snug">GSTIN/UIN: {shipToCustomer.gstNo ?? 'NA'}</p>
                  <p className="text-xs text-gray-600 leading-snug">State Name: {shipToCustomer.stateName ?? 'NA'}, Code: {shipToCustomer.stateCode ?? 'NA'}</p>
                </div>
              ) : (
                <p className="text-xs text-gray-500">—</p>
              )}
            </div>
            <div>
              <p className="font-semibold mb-2">Buyer (Bill to):</p>
              {billToCustomer ? (
                <div>
                  <p className="text-sm font-medium text-gray-900">{billToCustomer.customerName}</p>
                  <p className="text-xs text-gray-600 leading-snug mt-0.5">{billToCustomer.address}</p>
                  <p className="text-xs text-gray-600 leading-snug">Vendor Code: {billToCustomer.vendorCode ?? 'NA'}</p>
                  <p className="text-xs text-gray-600 leading-snug">GSTIN/UIN: {billToCustomer.gstNo ?? 'NA'}</p>
                  <p className="text-xs text-gray-600 leading-snug">State Name: {billToCustomer.stateName ?? 'NA'}, Code: {billToCustomer.stateCode ?? 'NA'}</p>
                </div>
              ) : (
                <p className="text-xs text-gray-500">—</p>
              )}
            </div>
          </div>

          {/* Right: Invoice particulars — one line per field (Label: value) */}
          <div className="invoice-meta-lines min-w-0 border border-gray-300 rounded overflow-hidden text-[11px] leading-snug divide-y divide-gray-300">
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Invoice No.: </span>
              <span className="text-gray-800">{invoice.invoiceNo ?? ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Dated: </span>
              <span className="text-gray-800">{invoice.invoiceDate ? formatDate(invoice.invoiceDate) : ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">e-Way Bill No.: </span>
              <span className="text-gray-800">{invoice.ewayBillNo ?? ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Delivery Note: </span>
              <span className="text-gray-800">{invoice.deliverNote ?? ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Reference No. &amp; Date: </span>
              <span className="text-gray-800">{invoice.referenceNoAndDate ?? ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Other References: </span>
              <span className="text-gray-800">{invoice.otherReferences ?? ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Buyer&apos;s Order No.: </span>
              <span className="text-gray-800">{invoice.buyerOrderNo ?? ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Buyer order dated: </span>
              <span className="text-gray-800">{invoice.buyerOrderDate ? formatDate(invoice.buyerOrderDate) : ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Dispatch Doc No.: </span>
              <span className="text-gray-800">{invoice.dispatchDocNo ?? ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Delivery Note Date: </span>
              <span className="text-gray-800">{invoice.deliverNoteDate ? formatDate(invoice.deliverNoteDate) : ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Dispatched through: </span>
              <span className="text-gray-800">{invoice.dispatchedThrough ?? ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Destination: </span>
              <span className="text-gray-800">{invoice.destination ?? ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Bill of Lading / LR-RR No.: </span>
              <span className="text-gray-800">{invoice.billOfLanding ?? ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Motor Vehicle No.: </span>
              <span className="text-gray-800">{invoice.motorVehicleNo ?? ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Term of delivery: </span>
              <span className="text-gray-800">{invoice.termOfDelivery ?? ''}</span>
            </div>
          </div>
        </div>

        {/* Itemized table - only show when there are line items */}
        {details.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300 text-center text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-2 py-2">#</th>
                <th className="border border-gray-300 px-2 py-2">Description of Goods</th>
                <th className="border border-gray-300 px-2 py-2">Boxes</th>
                <th className="border border-gray-300 px-2 py-2 min-w-[120px]">Heat No/Container</th>
                <th className="border border-gray-300 px-2 py-2">HSN Code</th>
                <th className="border border-gray-300 px-2 py-2">Quantity</th>
                <th className="border border-gray-300 px-2 py-2">Rate</th>
                <th className="border border-gray-300 px-2 py-2">Per</th>
                <th className="border border-gray-300 px-2 py-2">Amount</th>
              </tr>
            </thead>
            <tbody>
              {details.map((item, i) => (
                <tr key={item.id}>
                  <td className="border border-gray-300 px-2 py-1">{i + 1}</td>
                  <td className="border border-gray-300 px-2 py-1 text-left">{item.endgoodName}</td>
                  <td className="border border-gray-300 px-2 py-1">{item.boxes ?? item.packingDetailIds?.length ?? '—'}</td>
                  <td className="border border-gray-300 px-2 py-1">{item.userProvidedHeatNo || item.heatNo || '—'}</td>
                  <td className="border border-gray-300 px-2 py-1">{item.hsn ?? '—'}</td>
                  <td className="border border-gray-300 px-2 py-1">{item.qty}</td>
                  <td className="border border-gray-300 px-2 py-1">{item.rate}</td>
                  <td className="border border-gray-300 px-2 py-1">{item.uom ?? 'Nos'}</td>
                  <td className="border border-gray-300 px-2 py-1">{(item.qty * item.rate).toFixed(2)}</td>
                </tr>
              ))}
              <>
                <tr>
                  <td colSpan={5} className="border border-gray-300 px-2 py-1 text-right font-semibold"></td>
                  <td className="border border-gray-300"></td>
                  <td colSpan={2} className="border border-gray-300 text-right font-semibold"></td>
                  <td className="border border-gray-300 text-right font-semibold">{calc.taxableAmount.toFixed(2)}</td>
                </tr>
                {!isGst && (
                  <>
                    <tr>
                      <td colSpan={4} className="border border-gray-300 text-right font-semibold">CGST</td>
                      <td colSpan={4} className="border border-gray-300 text-right font-semibold"></td>
                      <td className="border border-gray-300 text-right font-semibold">{calc.totalCgst.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td colSpan={4} className="border border-gray-300 text-right font-semibold">SGST</td>
                      <td colSpan={4} className="border border-gray-300 text-right font-semibold"></td>
                      <td className="border border-gray-300 text-right font-semibold">{calc.totalSgst.toFixed(2)}</td>
                    </tr>
                  </>
                )}
                {isGst && (
                  <tr>
                    <td colSpan={4} className="border border-gray-300 text-right font-semibold">IGST</td>
                    <td colSpan={4} className="border border-gray-300 text-right font-semibold"></td>
                    <td className="border border-gray-300 text-right font-semibold">{calc.totalIgst.toFixed(2)}</td>
                  </tr>
                )}
                {otherChargesList.map((oc) => (
                  <tr key={oc.id}>
                    <td colSpan={4} className="border border-gray-300 text-right font-semibold">
                      {oc.chargeName ?? oc.chargeMaster?.chargeName ?? ''} ({oc.chargeDescription ?? oc.chargeMaster?.description ?? ''})
                    </td>
                    <td colSpan={4} className="border border-gray-300"></td>
                    <td className="border border-gray-300 text-right font-semibold">{(oc.amount ?? 0).toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="bg-gray-100 font-semibold">
                  <td colSpan={4} className="border border-gray-300 text-right py-2">TOTAL</td>
                  <td className="border border-gray-300"></td>
                  <td className="border border-gray-300 text-center">{calc.totalQty}</td>
                  <td colSpan={2} className="border border-gray-300"></td>
                  <td className="border border-gray-300 text-right">₹ {calc.totalAmount.toFixed(2)}</td>
                </tr>
              </>
            </tbody>
          </table>
        </div>
        ) : (
          <p className="text-sm text-gray-500 py-4">No line items.</p>
        )}

        <div className="mt-4 border-t pt-4">
          <p className="text-sm"><strong>Amount Chargeable (in words):</strong> INR {amountInWords(calc.totalAmount)}</p>
        </div>

        {/* HSN Summary */}
        <div className="mt-4 border rounded overflow-hidden">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="border border-gray-300 p-2 text-center">HSN</th>
                <th className="border border-gray-300 p-2 text-center">Taxable Value</th>
                {!isGst && (
                  <>
                    <th className="border border-gray-300 p-2 text-center">CGST Rate</th>
                    <th className="border border-gray-300 p-2 text-center">CGST Amount</th>
                    <th className="border border-gray-300 p-2 text-center">SGST Rate</th>
                    <th className="border border-gray-300 p-2 text-center">SGST Amount</th>
                  </>
                )}
                {isGst && (
                  <>
                    <th className="border border-gray-300 p-2 text-center">IGST Rate</th>
                    <th className="border border-gray-300 p-2 text-center">IGST Amount</th>
                  </>
                )}
                <th className="border border-gray-300 p-2 text-center">Total Tax Amount</th>
              </tr>
            </thead>
            <tbody>
              {hsnWiseTaxDetails.map((row, idx) => (
                <tr key={idx} className="border-b">
                  <td className="border border-gray-300 p-2">{row.hsn}</td>
                  <td className="border border-gray-300 p-2">{row.taxableValue.toFixed(2)}</td>
                  {!isGst && (
                    <>
                      <td className="border border-gray-300 p-2 text-center">{toDisplayRate(row.cgstRate).toFixed(2)}%</td>
                      <td className="border border-gray-300 p-2 text-right">{row.cgstAmount.toFixed(2)}</td>
                      <td className="border border-gray-300 p-2 text-center">{toDisplayRate(row.sgstRate).toFixed(2)}%</td>
                      <td className="border border-gray-300 p-2 text-right">{row.sgstAmount.toFixed(2)}</td>
                    </>
                  )}
                  {isGst && (
                    <>
                      <td className="border border-gray-300 p-2 text-center">{toDisplayRate(row.igstRate).toFixed(2)}%</td>
                      <td className="border border-gray-300 p-2 text-right">{row.igstAmount.toFixed(2)}</td>
                    </>
                  )}
                  <td className="border border-gray-300 p-2 text-right">{row.totalTaxAmount.toFixed(2)}</td>
                </tr>
              ))}
              <tr className="bg-gray-50 font-semibold">
                <td className="border border-gray-300 p-2">TOTAL</td>
                <td className="border border-gray-300 p-2">{calc.taxableAmount.toFixed(2)}</td>
                {!isGst && (
                  <>
                    <td className="border border-gray-300 p-2"></td>
                    <td className="border border-gray-300 p-2 text-right">{calc.totalCgst.toFixed(2)}</td>
                    <td className="border border-gray-300 p-2"></td>
                    <td className="border border-gray-300 p-2 text-right">{calc.totalSgst.toFixed(2)}</td>
                  </>
                )}
                {isGst && (
                  <>
                    <td className="border border-gray-300 p-2"></td>
                    <td className="border border-gray-300 p-2 text-right">{calc.totalIgst.toFixed(2)}</td>
                  </>
                )}
                <td className="border border-gray-300 p-2 text-right">{calc.totalTax.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-4 border rounded p-4">
          <p className="text-sm"><strong>Tax Amount (in words):</strong> INR {amountInWords(calc.totalTax)}</p>
          <p className="font-semibold mt-2">Company's PAN: {branchDetails?.panNo ?? 'NA'}</p>
        </div>
        <div className="mt-4 flex justify-between border-t pt-4">
          <div>
            <p className="font-semibold">Declaration:</p>
            <p className="text-sm text-gray-700">We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium">Authorized Signatory</p>
          </div>
        </div>
              </div>
            </div>
            {copyIndex < copies - 1 && (
              <div className="hidden print:block" style={{ pageBreakAfter: 'always' }} />
            )}
          </React.Fragment>
        );
      })}
      </div>
      )}

      {isEInvoiced && (
        <div className="w-full max-w-none min-w-0 overflow-x-auto print:overflow-visible">
          <InvoiceChunkedPrint
            invoice={invoice}
            branchDetails={branchDetails}
            shipToCustomer={shipToCustomer}
            billToCustomer={billToCustomer}
            details={details}
            otherChargesList={otherChargesList}
            calc={calc}
            hsnWiseTaxDetails={hsnWiseTaxDetails}
            isGst={isGst}
            qrValue={qrValue}
            copies={copies}
            showEwayBillOnView={showEwayBillOnView}
            digitallySignedByName={einvoiceSignerName}
          />
        </div>
      )}
    </div>
  );
};

export default InvoiceViewPage;
